export const COUNTRY_CODE_OPTIONS = [
  { country: "Philippines", code: "+63", placeholder: "912 345 6789" },
  { country: "United States", code: "+1", placeholder: "Phone number" },
  { country: "Canada", code: "+1", placeholder: "Phone number" },
  { country: "United Kingdom", code: "+44", placeholder: "Phone number" },
  { country: "Australia", code: "+61", placeholder: "Phone number" },
  { country: "Japan", code: "+81", placeholder: "Phone number" },
  { country: "South Korea", code: "+82", placeholder: "Phone number" },
  { country: "Singapore", code: "+65", placeholder: "Phone number" },
  { country: "Malaysia", code: "+60", placeholder: "Phone number" },
  { country: "Thailand", code: "+66", placeholder: "Phone number" },
  { country: "Indonesia", code: "+62", placeholder: "Phone number" },
  { country: "Vietnam", code: "+84", placeholder: "Phone number" },
  { country: "Hong Kong", code: "+852", placeholder: "Phone number" },
  { country: "Taiwan", code: "+886", placeholder: "Phone number" },
  { country: "UAE", code: "+971", placeholder: "Phone number" },
  { country: "Saudi Arabia", code: "+966", placeholder: "Phone number" },
];

const COUNTRY_PHONE_RULES = {
  "+1": { min: 10, max: 10 },
  "+44": { min: 7, max: 10 },
  "+60": { min: 7, max: 10 },
  "+61": { min: 7, max: 9 },
  "+62": { min: 8, max: 11 },
  "+63": { min: 10, max: 10, localPrefix: "0", localMin: 11, localMax: 11 },
  "+65": { min: 8, max: 8 },
  "+66": { min: 8, max: 9 },
  "+81": { min: 9, max: 10 },
  "+82": { min: 8, max: 10 },
  "+84": { min: 8, max: 9 },
  "+852": { min: 8, max: 8 },
  "+886": { min: 8, max: 9 },
  "+966": { min: 8, max: 9 },
  "+971": { min: 8, max: 9 },
};

export function getCountryOptionLabel(option) {
  return `${option.country} (${option.code})`;
}

export function normalizeLocalPhoneNumber(value) {
  return String(value || "").replace(/\D/g, "");
}

function getPhoneRule(countryCode) {
  const code = String(countryCode || "").trim();
  const codeDigits = code.replace(/\D/g, "");
  return COUNTRY_PHONE_RULES[code] || { min: 7, max: Math.max(7, 15 - codeDigits.length) };
}

export function getMaxLocalPhoneDigits(countryCode, value = "") {
  const rule = getPhoneRule(countryCode);
  const digits = normalizeLocalPhoneNumber(value);
  if (rule.localPrefix && digits.startsWith(rule.localPrefix)) {
    return rule.localMax || rule.max;
  }
  return rule.max;
}

export function normalizeLocalPhoneNumberForCountry(value, countryCode) {
  const digits = normalizeLocalPhoneNumber(value);
  return digits.slice(0, getMaxLocalPhoneDigits(countryCode, digits));
}

export function combineInternationalContact(countryCode, phoneNumber) {
  const code = String(countryCode || "").trim();
  const digits = normalizeLocalPhoneNumber(phoneNumber);
  if (!code || !digits) return "";

  const localDigits = code === "+63" && digits.startsWith("0")
    ? digits.slice(1)
    : digits;
  return `${code}${localDigits}`;
}

export function inferContactParts(value) {
  const clean = String(value || "").trim();
  const digits = normalizeLocalPhoneNumber(clean);
  const defaultParts = {
    contactCountryCode: "+63",
    contactNumber: "",
  };

  if (!clean) return defaultParts;

  const byCodeLength = [...COUNTRY_CODE_OPTIONS].sort(
    (a, b) => b.code.length - a.code.length
  );
  const matched = byCodeLength.find((option) => clean.startsWith(option.code));
  if (matched) {
    return {
      contactCountryCode: matched.code,
      contactNumber: normalizeLocalPhoneNumberForCountry(
        clean.slice(matched.code.length),
        matched.code
      ),
    };
  }

  if (digits.startsWith("63") && digits.length > 7) {
    return { contactCountryCode: "+63", contactNumber: digits.slice(2, 12) };
  }

  return {
    ...defaultParts,
    contactNumber: normalizeLocalPhoneNumberForCountry(clean, defaultParts.contactCountryCode),
  };
}

export function getContactNumberError(countryCode, value) {
  const code = String(countryCode || "").trim();
  const digits = normalizeLocalPhoneNumber(value);

  if (!code) return "Country code is required.";
  if (!digits) return "Contact number is required.";

  const rule = getPhoneRule(code);
  if (rule.localPrefix && digits.startsWith(rule.localPrefix)) {
    if (digits.length < rule.localMin) {
      return "This number is too short for the selected country code.";
    }
    if (digits.length > rule.localMax) {
      return "This number is too long for the selected country code.";
    }
    return "";
  }

  if (digits.length < rule.min) {
    return "This number is too short for the selected country code.";
  }
  if (digits.length > rule.max) {
    return "This number is too long for the selected country code.";
  }

  return "";
}

export function isValidPhoneForCountry(countryCode, value) {
  return !getContactNumberError(countryCode, value);
}
